import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface InnovationRow {
  readonly innovationId: string;
  readonly innovationCode: string;
  readonly title: string;
  readonly innovationCategory: string;
  readonly stage: string;
}

const CATEGORIES = [
  '', 'PROCESS', 'PRODUCT', 'TECHNOLOGY', 'BUSINESS_MODEL',
  'CUSTOMER_EXPERIENCE', 'OPERATIONAL', 'OTHER',
];
const STAGES = ['', 'PIPELINE', 'REVIEW', 'APPROVED', 'ADOPTION', 'ADOPTED', 'REJECTED'];
const CREATE_CATEGORIES = CATEGORIES.filter(Boolean);

/**
 * M33 — Innovation Management Dashboard (§31 / §20). Register of innovation
 * pipeline entries (T445) with innovation category and the pipeline→review→
 * approve→adoption→adopted stage lifecycle. Supports raising an innovation and
 * driving it — review, approve, start-adoption, mark-adopted and reject — each
 * wired to the guarded /v1/pm/innovations write API. Filterable by category and
 * stage.
 */
export function InnovationManagementDashboard(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<InnovationRow[]>([]);
  const [innovationCategory, setInnovationCategory] = useState('');
  const [stage, setStage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [newTitle, setNewTitle] = useState('');
  const [newCategory, setNewCategory] = useState('PROCESS');
  const [rejReasons, setRejReasons] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (innovationCategory) params.set('innovationCategory', innovationCategory);
    if (stage) params.set('stage', stage);
    const qs = params.toString();
    apiRequest<{ data: InnovationRow[] }>(`/v1/pm/innovations${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, innovationCategory, stage, reload]);

  const failMessage = (err: unknown, perm: string): string =>
    err instanceof ApiError && err.status === 403
      ? t('m33.common.notAuthorised', { perm })
      : (err as Error).message;

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!newTitle.trim()) { setActionError(t('m33.innovation.validation')); return; }
    setBusy(true);
    try {
      await apiRequest(`/v1/pm/innovations`, {
        method: 'POST',
        body: { title: newTitle.trim(), innovationCategory: newCategory },
        token,
      });
      setNotice(t('m33.innovation.raised', { name: newTitle.trim() }));
      setNewTitle('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, 'INNOVATION_MANAGE'));
    } finally {
      setBusy(false);
    }
  };

  const act = async (r: InnovationRow, path: string, label: string, perm: string, body?: Record<string, unknown>): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/pm/innovations/${r.innovationId}/${path}`, { method: 'POST', body: body ?? {}, token });
      setNotice(t('m33.common.notice', { code: r.innovationCode, label }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, perm));
    }
  };

  return (
    <main aria-label="innovation-management-dashboard">
      <h1>{t('m33.innovation.title')}</h1>

      <section aria-label="raise-innovation">
        <h2>{t('m33.innovation.raiseHeading')}</h2>
        <form aria-label="innovation-form" onSubmit={(e) => void create(e)}>
          <label>{t('m33.common.title')}<input aria-label="field-innovation-title" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} /></label>
          <label>{t('m33.common.category')}
            <select aria-label="field-innovation-category" value={newCategory} onChange={(e) => setNewCategory(e.target.value)}>
              {CREATE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <Button variant="primary" type="submit" aria-label="submit-innovation" disabled={busy}>{busy ? t('m33.innovation.raising') : t('m33.innovation.raiseBtn')}</Button>
        </form>
        {actionError && <p role="alert" aria-label="innovation-error">{actionError}</p>}
        {notice && <p aria-label="innovation-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {t('m33.common.category')}
          <select aria-label="filter-category" value={innovationCategory} onChange={(e) => setInnovationCategory(e.target.value)}>
            {CATEGORIES.map((c) => <option key={c || 'all'} value={c}>{c || t('m33.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m33.innovation.stage')}
          <select aria-label="filter-stage" value={stage} onChange={(e) => setStage(e.target.value)}>
            {STAGES.map((s) => <option key={s || 'all'} value={s}>{s || t('m33.common.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m33.common.loading')}</p> : (
        <>
          <p aria-label="innovation-count">{t('m33.innovation.count', { n: rows.length })}</p>
          <DataTable
            ariaLabel="innovation-table"
            rows={rows}
            rowKey={(r) => r.innovationId}
            columns={[
              { key: 'innovationCode', header: t('m33.common.code'), render: (r) => r.innovationCode },
              { key: 'title', header: t('m33.common.title'), render: (r) => r.title },
              { key: 'innovationCategory', header: t('m33.common.category'), render: (r) => r.innovationCategory },
              { key: 'stage', header: t('m33.innovation.stage'), render: (r) => r.stage },
              { key: 'actions', header: t('m33.common.actions'), render: (r) => (
                <>
                  {r.stage === 'PIPELINE' && (
                    <Button variant="secondary" type="button" aria-label={`review-${r.innovationCode}`} onClick={() => void act(r, 'review', t('m33.innovation.act.movedToReview'), 'INNOVATION_MANAGE')}>{t('m33.innovation.btnReview')}</Button>
                  )}
                  {r.stage === 'REVIEW' && (
                    <Button variant="secondary" type="button" aria-label={`approve-${r.innovationCode}`} onClick={() => void act(r, 'approve', t('m33.innovation.act.approved'), 'INNOVATION_APPROVE')}>{t('m33.innovation.btnApprove')}</Button>
                  )}
                  {r.stage === 'APPROVED' && (
                    <Button variant="secondary" type="button" aria-label={`start-adoption-${r.innovationCode}`} onClick={() => void act(r, 'start-adoption', t('m33.innovation.act.adoptionStarted'), 'INNOVATION_MANAGE')}>{t('m33.innovation.btnStartAdoption')}</Button>
                  )}
                  {r.stage === 'ADOPTION' && (
                    <Button variant="secondary" type="button" aria-label={`mark-adopted-${r.innovationCode}`} onClick={() => void act(r, 'mark-adopted', t('m33.innovation.act.markedAdopted'), 'INNOVATION_MANAGE')}>{t('m33.innovation.btnMarkAdopted')}</Button>
                  )}
                  {!['ADOPTED', 'REJECTED'].includes(r.stage) && (
                    <>
                      <input aria-label={`reject-reason-${r.innovationCode}`} value={rejReasons[r.innovationId] ?? ''} onChange={(e) => setRejReasons((m) => ({ ...m, [r.innovationId]: e.target.value }))} placeholder={t('m33.common.reason')} />
                      <Button variant="danger" type="button" aria-label={`reject-${r.innovationCode}`} onClick={() => void act(r, 'reject', t('m33.innovation.act.rejected'), 'INNOVATION_APPROVE', { reason: rejReasons[r.innovationId] ?? '' })}>{t('m33.innovation.btnReject')}</Button>
                    </>
                  )}
                </>
              ) },
            ]}
          />
        </>
      )}
    </main>
  );
}
